import { Box, TextField, Typography, IconButton, Button, Stack, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { AssetRetrieval } from "types/assetRetrieval";
import { Print, Edit, CheckCircle, KeyboardArrowDown, CopyAll } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link } from "react-router-dom";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { api } from "services/api.service";
import { useAlert } from "hooks/useAlert";
import { useState } from "react";
import { Formik, Form as FormikForm } from "formik";
interface AssetRetrievalRowProps {
  row: AssetRetrieval;
  submit: (assetRetrievalsUuid: string, params: { type: string; trackingNumber: string }) => void;
  update: (assetRetrievalsUuid: string, shipmentUuid: string, params: { type: string; trackingNumber: string }) => void;
}

const INSURANCE_OPTIONS_DISPLAY: { [number: number]: number } = {
  0: 0,
  10: 1000,
  20: 2000,
  30: 3000,
  40: 4000,
  50: 5000,
};

export default function AssetRetrievalRow({ row, submit, update }: AssetRetrievalRowProps) {
  const { enabled } = useFeatureFlag("update_retrievals");
  const { alertSuccess, alertError } = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Stack
        sx={{
          my: 1,
          border: "1px solid",
          borderColor: "divider",
          mx: 4,
          borderRadius: 2,
          marginY: 4,
          background: "background.paper",
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "0.8fr 1fr 1fr 0.7fr", my: 3, mx: 3, columnGap: 1 }}>
          <Stack>
            <Typography sx={{ opacity: 60 }} color="GrayText" textTransform="uppercase">
              to label details
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", my: 2, columnGap: 1 }}>
              <Stack>
                <Typography>
                  {row.employeeFirstName} {row.employeeLastName}
                </Typography>
                <Typography>{row.employeeEmail}</Typography>
                <Typography>{row.employeePhone}</Typography>
              </Stack>
              <Stack>
                <Typography>{row.employeeAddress}</Typography>
                <Typography>{row.employeeAddressTwo}</Typography>
                <Typography>
                  {row.employeeAddressCity}, {row.employeeAddressState} {row.employeeAddressZipCode}
                </Typography>
                <Typography>{row.employeeAddressCountry}</Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack>
            <Typography sx={{ opacity: 60 }} color="GrayText" textTransform="uppercase">
              return label details
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", my: 2, rowGap: 2, columnGap: 1 }}>
              <Stack>
                {row.returnRecipientFirstName ? (
                  <>
                    <Typography>
                      {row.returnRecipientFirstName} {row.returnRecipientLastName}
                    </Typography>
                    <Typography>{row.returnRecipientCompany}</Typography>
                  </>
                ) : (
                  <>
                    <Typography>
                      {row.user
                        ? `${row.user.firstName} ${row.user.lastName}`
                        : `API order ${row.organization.name} returning to Revivn`}
                    </Typography>
                    <Typography>{row.user ? row.user.email : ""}</Typography>
                  </>
                )}
              </Stack>
              <Stack>
                <Typography>{row.organization.name}</Typography>
                {row.location ? (
                  <>
                    <Typography>{row.location.address}</Typography>
                    <Typography>{row.location.address2}</Typography>
                    <Typography>
                      {row.location.city}, {row.location.state} {row.location.zipcode}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography>{row.returnRecipientAddress}</Typography>
                    <Typography>{row.returnRecipientAddressTwo}</Typography>
                    <Typography>
                      {row.returnRecipientAddressCity}, {row.returnRecipientAddressState}{" "}
                      {row.returnRecipientAddressZipCode}
                    </Typography>
                    <Typography>{row.returnRecipientAddressCountry}</Typography>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
          <Stack>
            <Typography sx={{ opacity: 60 }} color="GrayText" textTransform="uppercase">
              Device details
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", my: 2, rowGap: 2, columnGap: 1 }}>
              <Stack>
                <Typography>
                  Insurance Value: $
                  {row.insuranceOption ? INSURANCE_OPTIONS_DISPLAY[row.insuranceOption] : row.insuranceOption}
                </Typography>
              </Stack>
              <Stack>
                <Typography>{row.manufacturer}</Typography>
                <Typography>{row.model}</Typography>
                <Typography>{row.serialNumber}</Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack sx={{ rowGap: 1 }}>
            <Typography sx={{ opacity: 60 }} color="GrayText">
              To - Tracking Number #:{" "}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Formik
                initialValues={{
                  outboundAssetRetrievalShipment: row.outboundAssetRetrievalShipment?.trackingNumber || "",
                }}
                onSubmit={(values) => {
                  const payload = {
                    type: "OutboundAssetRetrievalShipment",
                    trackingNumber: values.outboundAssetRetrievalShipment,
                  };
                  if (row.outboundAssetRetrievalShipment) {
                    update(row.uuid, row.outboundAssetRetrievalShipment.uuid, payload);
                  } else {
                    submit(row.uuid, payload);
                  }
                }}
              >
                {({ handleSubmit, getFieldProps }) => (
                  <FormikForm>
                    <TextField
                      size="small"
                      placeholder="To Label"
                      disabled={row.state !== "order_placed"}
                      {...getFieldProps("outboundAssetRetrievalShipment")}
                      onBlur={() => handleSubmit()}
                    />
                  </FormikForm>
                )}
              </Formik>
              <IconButton
                aria-label="print"
                disabled={!row.outboundAssetRetrievalShipment?.shippingLabelPdfUrl}
                sx={{ my: 1 }}
                color="primary"
                onClick={() => {
                  window.open(row.outboundAssetRetrievalShipment.shippingLabelPdfUrl, "_blank");
                }}
              >
                <Print />
              </IconButton>
            </Stack>

            <Typography sx={{ opacity: 60 }} color="GrayText">
              Return - Tracking Number #:{" "}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Formik
                initialValues={{
                  inboundAssetRetrievalShipment: row.inboundAssetRetrievalShipment?.trackingNumber || "",
                }}
                onSubmit={(values) => {
                  const payload = {
                    type: "InboundAssetRetrievalShipment",
                    trackingNumber: values.inboundAssetRetrievalShipment,
                  };
                  if (row.inboundAssetRetrievalShipment) {
                    update(row.uuid, row.inboundAssetRetrievalShipment.uuid, payload);
                  } else {
                    submit(row.uuid, payload);
                  }
                }}
              >
                {({ handleSubmit, getFieldProps }) => (
                  <FormikForm>
                    <TextField
                      size="small"
                      placeholder="Return Label"
                      disabled={row.state !== "order_placed"}
                      {...getFieldProps("inboundAssetRetrievalShipment")}
                      onBlur={() => handleSubmit()}
                    />
                  </FormikForm>
                )}
              </Formik>
              <IconButton
                aria-label="print"
                color="primary"
                disabled={!row.inboundAssetRetrievalShipment?.shippingLabelPdfUrl}
                onClick={() => {
                  window.open(row.inboundAssetRetrievalShipment.shippingLabelPdfUrl, "_blank");
                }}
              >
                <Print />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
        <Stack margin={2} direction="row" justifyContent="space-between">
          <Stack>
            {row.labelsPrintedAt && (
              <Typography>
                Labels printed at <b>{new Date(Date.parse(row.labelsPrintedAt)).toLocaleString()}</b>
              </Typography>
            )}
            {row.packageOutToEmployeeAt && (
              <Typography>
                Package shipped to employee at{" "}
                <b>{new Date(Date.parse(row.packageOutToEmployeeAt)).toLocaleString()}</b>
              </Typography>
            )}
            {row.orderCancelledAt && (
              <Typography>
                Order cancelled by <b>{row.canceledByEmail}</b> due to <b>{row.cancellationReason}</b> at{" "}
                <b>{new Date(Date.parse(row.orderCancelledAt)).toLocaleString()}</b>
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            {row.lastSyncedAt && (
              <Typography variant="caption" alignSelf="center">
                Last synced {new Date(row.lastSyncedAt).toLocaleString()}
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleClick}
              endIcon={<KeyboardArrowDown />}
            >
              Actions
            </Button>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
              {enabled && (
                <MenuItem component={Link} to={`${row.uuid}`} style={{ textDecoration: "none" }}>
                  <ListItemIcon>
                    <Edit />
                  </ListItemIcon>
                  Update addresses
                </MenuItem>
              )}
              <MenuItem
                onClick={() =>
                  api
                    .post(`asset_retrievals/${row.uuid}/sync`)
                    .then(() => alertSuccess("Order status sync queued. The order status will update shortly."))
                    .catch((error) => alertError(error.response?.data?.errors || "Failed to sync order status"))
                }
              >
                <ListItemIcon>
                  <RefreshIcon />
                </ListItemIcon>
                Sync order status
              </MenuItem>
              {row.state.startsWith("package") && (
                <MenuItem component={Link} to={`${row.uuid}/force_complete`}>
                  <ListItemIcon>
                    <CheckCircle />
                  </ListItemIcon>
                  Mark as completed
                </MenuItem>
              )}
              {!row.obfuscated &&
                ["labels_printed", "package_out_to_employee", "package_received_by_employee"].includes(row.state) && (
                  <MenuItem component={Link} to={`${row.uuid}/regenerate-labels`}>
                    <ListItemIcon>
                      <CopyAll />
                    </ListItemIcon>
                    Regenerate labels
                  </MenuItem>
                )}
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
